import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = 'https://stu148664.webhosting.arden.ac.uk/COM6008/api';

// Async thunks
export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (params = {}) => {
    const { data } = await axios.get(`${API_BASE_URL}/transactions`, { params });
    return data;
  }
);

export const createTransaction = createAsyncThunk(
  'transactions/createTransaction',
  async (transactionData) => {
    const { data } = await axios.post(`${API_BASE_URL}/transactions`, transactionData);
    return data;
  }
);

export const updateTransaction = createAsyncThunk(
  'transactions/updateTransaction',
  async ({ id, ...transactionData }) => {
    const { data } = await axios.put(`${API_BASE_URL}/transactions/${id}`, transactionData);
    return data;
  }
);

export const deleteTransaction = createAsyncThunk(
  'transactions/deleteTransaction',
  async (id) => {
    await axios.delete(`${API_BASE_URL}/transactions/${id}`);
    return id;
  }
);

export const fetchCategories = createAsyncThunk(
  'transactions/fetchCategories',
  async () => {
    const { data } = await axios.get(`${API_BASE_URL}/categories`);
    return data;
  }
);

export const fetchTransactionAnalytics = createAsyncThunk(
  'transactions/fetchAnalytics',
  async (timeframe = 'month') => {
    const { data } = await axios.get(`${API_BASE_URL}/analytics/transactions`, {
      params: { timeframe }
    });
    return data;
  }
);

const initialState = {
  transactions: [],
  categories: [],
  analytics: {
    totalIncome: 0,
    totalExpenses: 0,
    netIncome: 0,
    categoryBreakdown: [],
    monthlyTrends: [],
    topExpenseCategories: [],
  },
  filters: {
    searchQuery: '',
    categoryId: null,
    transactionType: 'all',
    dateRange: null,
    sortBy: 'transaction_date',
    sortOrder: 'desc',
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  loading: {
    transactions: false,
    categories: false,
    analytics: false,
    creating: false,
    updating: false,
    deleting: false,
  },
  error: null,
  selectedTransaction: null,
};

const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setSelectedTransaction: (state, action) => {
      state.selectedTransaction = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    optimisticUpdate: (state, action) => {
      const { id, updates } = action.payload;
      const transactionIndex = state.transactions.findIndex(t => t.id === id);
      if (transactionIndex !== -1) {
        state.transactions[transactionIndex] = { 
          ...state.transactions[transactionIndex], 
          ...updates 
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.loading.transactions = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading.transactions = false;
        state.transactions = action.payload;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading.transactions = false;
        state.error = action.error.message;
      })
      
      // Create transaction
      .addCase(createTransaction.pending, (state) => {
        state.loading.creating = true;
        state.error = null;
      })
      .addCase(createTransaction.fulfilled, (state, action) => {
        state.loading.creating = false;
        state.transactions.unshift(action.payload);
      })
      .addCase(createTransaction.rejected, (state, action) => {
        state.loading.creating = false;
        state.error = action.error.message;
      })
      
      // Update transaction
      .addCase(updateTransaction.pending, (state) => {
        state.loading.updating = true;
        state.error = null;
      })
      .addCase(updateTransaction.fulfilled, (state, action) => {
        state.loading.updating = false;
        const index = state.transactions.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.loading.updating = false;
        state.error = action.error.message;
      })
      
      // Delete transaction
      .addCase(deleteTransaction.pending, (state) => {
        state.loading.deleting = true;
        state.error = null;
      })
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.loading.deleting = false;
        state.transactions = state.transactions.filter(t => t.id !== action.payload);
      })
      .addCase(deleteTransaction.rejected, (state, action) => {
        state.loading.deleting = false;
        state.error = action.error.message;
      })
      
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading.categories = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading.categories = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading.categories = false;
        state.error = action.error.message;
      })
      
      // Fetch analytics
      .addCase(fetchTransactionAnalytics.pending, (state) => {
        state.loading.analytics = true;
      })
      .addCase(fetchTransactionAnalytics.fulfilled, (state, action) => {
        state.loading.analytics = false;
        state.analytics = action.payload;
      })
      .addCase(fetchTransactionAnalytics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setSelectedTransaction,
  setPagination,
  clearError,
  optimisticUpdate,
} = transactionSlice.actions;

// Selectors
export const selectTransactions = (state) => state.transactions.transactions;
export const selectCategories = (state) => state.transactions.categories;
export const selectAnalytics = (state) => state.transactions.analytics;
export const selectFilters = (state) => state.transactions.filters;
export const selectLoading = (state) => state.transactions.loading;
export const selectError = (state) => state.transactions.error;
export const selectSelectedTransaction = (state) => state.transactions.selectedTransaction;

// Complex selectors
export const selectFilteredTransactions = (state) => {
  const { transactions, filters } = state.transactions;
  let filtered = [...transactions];

  if (filters.searchQuery) {
    filtered = filtered.filter(t => 
      t.description.toLowerCase().includes(filters.searchQuery.toLowerCase())
    );
  }

  if (filters.categoryId) {
    filtered = filtered.filter(t => t.category_id === filters.categoryId);
  }

  if (filters.transactionType !== 'all') {
    filtered = filtered.filter(t => t.transaction_type === filters.transactionType);
  }

  // Sort
  filtered.sort((a, b) => {
    const multiplier = filters.sortOrder === 'asc' ? 1 : -1;
    if (filters.sortBy === 'amount') {
      return (a.amount - b.amount) * multiplier;
    }
    if (filters.sortBy === 'transaction_date') {
      return (new Date(a.transaction_date) - new Date(b.transaction_date)) * multiplier;
    }
    return 0;
  });

  return filtered;
};

export const selectTransactionStats = (state) => {
  const transactions = selectFilteredTransactions(state);
  
  const totalIncome = transactions
    .filter(t => t.transaction_type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
    
  const totalExpenses = transactions
    .filter(t => t.transaction_type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);
    
  return {
    totalIncome,
    totalExpenses,
    netIncome: totalIncome - totalExpenses,
    transactionCount: transactions.length,
  };
};

export default transactionSlice.reducer; 